import type { Cell, Color, Game } from '../../types/game'
import { Worker } from '../shared/Worker'
import { ResourceIcon } from '../shared/ResourceIcon'
import { assetUrl } from '../../utils/assets'

interface HexCellProps {
  cell: Cell
  game: Game
  seed?: number
  gridRowStart: number
  gridColumnStart: number
  availableActions: string[]
  onActivateNesting: (cellId: string) => void
  onBuild: (cellId: string) => void
  onMakeHoney: (cellId: string) => void
  onLayEgg: (cellId: string) => void
  onClearInfection: (cellId: string) => void
}

export function HexCell({
  cell,
  game,
  seed,
  gridRowStart,
  gridColumnStart,
  availableActions,
  onActivateNesting,
  onBuild,
  onMakeHoney,
  onLayEgg,
  onClearInfection,
}: HexCellProps) {
  const workerColor = game.hive_workers[cell.id] as Color | undefined
  const honey = game.hive_honey[cell.id] ?? []
  const infections = game.hive_infections[cell.id] ?? 0
  const eggColor = game.hive_eggs[cell.id] as Color | undefined
  const hasQueen = cell.id === game.queen.cell_id
  const broodChamberIds = new Set((game.hive.brood_chambers ?? []).map((broodCell) => broodCell.id))
  const isBroodChamber = broodChamberIds.has(cell.id)

  let cellName = 'empty_cell'
  if (isBroodChamber) {
    if (honey.length > 0) {
      cellName = 'honey_cell'
    } else if (eggColor) {
      cellName = 'egg_cell'
    } else {
      cellName = 'brood_cell'
    }
  }

  const variant = seed ?? 1
  const isNested = Boolean(eggColor) || honey.length > 0

  const handleClick = () => {
    if (infections > 0) {
      onClearInfection(cell.id)
      return
    }
    if (isBroodChamber && !isNested) {
      onActivateNesting(cell.id)
      return
    }
    if (isBroodChamber && honey.length > 0) {
      onMakeHoney(cell.id)
      return
    }
    if (isBroodChamber) {
      onLayEgg(cell.id)
      return
    }
    onBuild(cell.id)
  }

  return (
    <div
      className="cell"
      data-cell-id={cell.id}
      style={{
        gridRow: `${gridRowStart} / span 4`,
        gridColumn: `${gridColumnStart} / span 2`,
      }}
      onClick={handleClick}
    >
      <img
        src={assetUrl('images', 'cells', `${cellName}_${variant}.svg`)}
        alt=""
        className="cell-img"
      />
      {cell.bonus && !isBroodChamber && (
        <div className="bonus">
          <div>+</div>
          <ResourceIcon resource={cell.bonus} />
        </div>
      )}
      {hasQueen && (
        <div className="queen">
          <img src={assetUrl('images', 'resources', 'queen.svg')} alt="" className="icon" />
        </div>
      )}
      <div className="infection-container">
        {Array.from({ length: infections }).map((_, index) => (
          <div key={index} className="infection" />
        ))}
      </div>
      {workerColor && <Worker color={workerColor} />}
      <div className="honey-container">
        {honey.map((color, index) => (
          <ResourceIcon
            key={`${cell.id}-honey-${index}`}
            resource="honey"
            className={`${color} honey`}
          />
        ))}
      </div>
      {eggColor && (
        <div className="egg-container">
          <ResourceIcon resource="egg" className={`${eggColor} egg`} />
        </div>
      )}
    </div>
  )
}
